// i18next-extract-mark-ns-start blog

import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {Content} from 'components/Content';
import {Form, Formik} from 'formik';
import {Trans, useI18next, useTranslation} from 'gatsby-plugin-react-i18next';
import {Field} from 'components/Field';
import * as yup from 'yup';
import {links} from 'global.config';
import PlaneSvg from 'images/blog-plane.svg';
import {ScreenSizes} from '../../../types/responsive';
import {getClientId} from 'utils/helpers';
import {Button} from '../Button';
import { InternalPageLink } from 'components/links/Pages';

const Container = styled.div`
  position: relative;
`;

const BannerBG = styled.div`
  background-color: #7984d9;
  mix-blend-mode: multiply;
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
`;

const BannerContent = styled(Content)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  padding: 25px 20px;
  background-image: url(${PlaneSvg});
  background-repeat: no-repeat;
  background-position-x: 100%;
  background-position-y: 65%;
  background-size: 400px;

  @media (max-width: ${ScreenSizes.lg}) {
    background-position-x: calc(100vw - 20px - 400px);
  }

  @media (max-width: ${ScreenSizes.md}) {
    background-position-x: calc(100vw - 20px - 350px);
    background-position-y: 80%;
    background-size: 350px;
  }
  @media (max-width: ${ScreenSizes.sm}) {
    background: none;
    width: 100%;
  }
`;

const BannerTitle = styled.h2`
  font-style: normal;
  font-weight: 300;
  font-size: 39px;
  color: #ffffff;
  white-space: nowrap;
  margin-bottom: 1rem;
  line-height: 125%;

  @media (max-width: ${ScreenSizes.md}) {
    width: 100%;
    white-space: normal;
  }
`;

const BannerBody = styled.p`
  max-width: 768px;
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 130%;
  letter-spacing: 0.02em;
  margin-bottom: 1rem;
  color: #ffffff;
`;

const FormContainer = styled.div`
  width: 100%;
`;

const TooltipContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 0.5rem;
  width: 100%;
  
  @media (min-width: ${ScreenSizes.sm}) {
    &:hover .tooltip-content {
      visibility: visible;
      opacity: 1;
    }
  }
`;

const LegalText = styled.span`
  color: #ffffff;
  font-size: 12px;
  text-decoration: underline;
  cursor: pointer;
`;

const TooltipContent = styled.div`
  visibility: hidden;
  opacity: 0;
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  padding: 1rem;
  background: #fff;
  color: #333;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 300px;
  font-size: 12px;
  line-height: 1.4;
  transition: all 0.2s ease;
  z-index: 10;
  margin-bottom: 10px;

  &.visible {
    visibility: visible;
    opacity: 1;
  }
  
  &:after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 8px;
    border-style: solid;
    border-color: #fff transparent transparent transparent;
  }

  a {
    color: #4353ff;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const BannerForm = styled(Form)`
  display: flex;
  gap: 1.5rem;
  flex-direction: column;
  align-items: flex-start;

  > div {
    margin: 0;
    width: 40%;
    @media (min-width: ${ScreenSizes.sm}) {
      min-width: 400px;
    }
    @media (max-width: ${ScreenSizes.sm}) {
      width: 100%;
    }
    > div {
      bottom: 16px;
      position: absolute;
    }
  }

  button {
    width: 40%;
    @media (min-width: ${ScreenSizes.sm}) {
      min-width: 400px;
    }
    @media (max-width: ${ScreenSizes.sm}) {
      width: 100%;
    }
  }
`;

type FormValues = {
  firstName: string;
  email: string;
};
const BlogBanner: React.FC = () => {
  const [isDone, setDone] = useState(false);
  const {language, path} = useI18next();
  const [isLoading, setLoading] = useState(false);
  const [userId, setUserId] = useState<string | undefined>(undefined);
  const [isTooltipVisible, setTooltipVisible] = useState(false);

  const {t} = useTranslation();

  useEffect(() => {
    getClientId().then((userId) => {
      setUserId(userId);
    });
  }, []);

  const handleSubmit = async ({firstName, email}: FormValues) => {
    setLoading(true);
    try {
      const body = JSON.stringify({
        email,
        firstName,
        topics: ['NewBlogPost'],
        language,
        userId
      });
      fetch(links.pinpointApi + `user/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body
      }).then(() => {
        setDone(true);
        setLoading(false);
      });
    } catch (error) {
      setLoading(false);
    }
  };

  const validationSchema = yup.object().shape({
    firstName: yup.string().required(t('Name is required')),
    email: yup.string().email(t('should be a valid email')).required(t('email is required'))
  });

  const handleLegalClick = () => {
    setTooltipVisible(!isTooltipVisible);
  };

  return (
    <Container>
      <BannerBG />
      <BannerContent>
        <BannerTitle>
          <Trans>Learn how to grow your business</Trans>
        </BannerTitle>
        <BannerBody>
          <Trans>Get payment insights and trends, expert business tips, and MONEI news.</Trans>
        </BannerBody>
        <Formik
          initialValues={{
            firstName: '',
            email: ''
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}>
          {
            <FormContainer>
              <BannerForm>
                {isDone ? (
                  <Trans parent="p">Thank you! We will keep in touch.</Trans>
                ) : (
                  <>
                    <Field
                      name="firstName"
                      className="email-field"
                      placeholder={t('Name')}
                      type="text"
                    />
                    <Field
                      name="email"
                      className="email-field"
                      placeholder={t('Email Address')}
                      type="email"
                    />

                    <Button type="submit" loading={isLoading} variant="dark">
                      <Trans>Subscribe</Trans>
                    </Button>
                    <TooltipContainer>
                      <LegalText onClick={handleLegalClick}>
                        <Trans>Privacy and data processing information</Trans>
                      </LegalText>
                      <TooltipContent className={`tooltip-content ${isTooltipVisible ? 'visible' : ''}`}>
                        <Trans>
                          MONEI DIGITAL PAYMENTS SL is responsible for processing your personal data,
                          the purpose of which is to manage your subscription to the MONEI Blog and
                          send you information and trends about payments, expert business advice, and
                          news about MONEI. You may exercise your rights of access, rectification,
                          deletion, opposition, limitation, and portability by postal mail to Calle
                          Palestina, 1, Entreplanta, 29007, Málaga, Spain or by email to{' '}
                          <a rel="noopener noreferrer" href="mailto:support@monei.com">
                            support@monei.com
                          </a>
                          , proving your identity. You can obtain more information about the
                          processing of your personal data in the{' '}
                          <InternalPageLink to="/privacy-policy">
                            Website Privacy Policy
                          </InternalPageLink>
                        </Trans>
                      </TooltipContent>
                    </TooltipContainer>
                  </>
                )}
              </BannerForm>
            </FormContainer>
          }
        </Formik>
      </BannerContent>
    </Container>
  );
};

export default BlogBanner;
